import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import './i18n'
import App from './App.tsx'
import { initErrorTracking } from './services/errorTracking'
import { initFirebase, getFirebaseAnalytics } from './services/firebase'
import { setAnalyticsUserProperties } from './services/analytics'
import { AppErrorBoundary } from './components/AppErrorBoundary/AppErrorBoundary'
import { initInstallApp } from './services/installApp'
import { initTheme } from './services/theme'
import { initTelemetry, telemetryStatus, purgeOldTelemetryEvents } from './services/telemetry'
import { countQueuedPhotos } from './services/offlineSyncClient'
import { getPendingDetails } from './services/db'
import { describeCoverage } from './services/networkStatus'
import { isEpisUrl, openEpis } from './services/episSso'

// Telemetría: inicializamos ANTES de montar React para que las primeras
// excepciones (incluso de carga de la app) ya queden capturadas.
initErrorTracking();

// Captura temprana de `beforeinstallprompt`: si lo registráramos solo al
// montar el componente que lo usa, podríamos perder el evento (se dispara
// una sola vez por carga).
initInstallApp();
// Suscribe el listener de prefers-color-scheme cuando la preferencia es
// 'auto'. La aplicación inicial del data-theme ya la hizo el script
// inline de index.html (anti-FOUC).
initTheme();
initFirebase().then(() => {
  // Telemetría en vivo (Realtime Database): misma estructura que la app
  // Android nativa, para verse junto a las tablets en el mismo visor. Se
  // distingue por plataforma 'pwa'. Best-effort: jamás bloquea la app, y sin
  // databaseURL configurada no hace nada.
  initTelemetry();
  // Status inicial con los RECUENTOS REALES leídos de IndexedDB (no solo el
  // estado): la tarjeta del visor arranca fiel —pendientes, fotos y
  // cobertura— aunque todavía no haya corrido ningún sync. Si IDB aún no
  // responde, se emite sin recuentos y la red de seguridad del coordinador lo
  // corrige al montar.
  getPendingDetails()
    .then((items) => {
      telemetryStatus({
        estado: 'app abierta',
        elementosPendientes: items.length,
        fotosPendientes: countQueuedPhotos(items),
        cobertura: describeCoverage(),
      });
    })
    .catch(() => {
      telemetryStatus({ estado: 'app abierta', cobertura: describeCoverage() });
    });
  // Retención de 48h del historial: en diferido para no competir con el
  // arranque y dar tiempo a que la auth anónima esté lista.
  setTimeout(purgeOldTelemetryEvents, 15000);
  if (getFirebaseAnalytics()) {
    // Propiedades de usuario que se mantienen entre eventos.
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    setAnalyticsUserProperties({
      app_version: __APP_VERSION__,
      pwa_installed: isStandalone,
      platform: 'web',
    });
  }
});

// Desactivar restauración de scroll del navegador — lo gestionamos manualmente
if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Si llegamos aquí desde una notificación push con URL externa, el SW nos
// abre la PWA con `?externalUrl=<encoded>`. El SW no puede abrir URLs
// cross-origin directamente; sí podemos hacerlo nosotros desde el cliente.
//
// Estrategia (de más a menos permisiva con popup blockers):
//   1. Anchor click sintético — Firefox y Safari lo aceptan mejor que
//      window.open() porque imita un click "real".
//   2. window.open(_blank) — fallback genérico.
//   3. location.href — último recurso: navega la pestaña actual y saca
//      al usuario de la PWA. Garantizado.
(() => {
  const params = new URLSearchParams(window.location.search);
  const ext = params.get('externalUrl');
  if (!ext) return;
  if (!/^https?:\/\//i.test(ext)) return; // por seguridad, solo http(s)
  params.delete('externalUrl');
  const newSearch = params.toString();
  const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
  window.history.replaceState(null, '', newUrl);

  // En iOS PWA standalone, navegar la pestaña actual a una URL fuera del
  // scope del manifest hace que iOS abra Safari automáticamente con un
  // banner "Volver a la app". Es lo más fiable y no depende del "gesto"
  // del usuario (que se pierde tras la navegación desde el SW).
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

  // Enlaces a la PWA de EPIs: con acceso directo para no repetir el login.
  if (isEpisUrl(ext)) {
    openEpis(ext, isStandalone);
    return;
  }

  if (isStandalone) {
    window.location.href = ext;
    return;
  }

  // En navegador normal, intentamos anchor click (más permisivo que
  // window.open en Firefox/Safari). Sin setTimeout para conservar el
  // contexto de "user activation".
  let opened = false;
  try {
    const a = document.createElement('a');
    a.href = ext;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    opened = true;
  } catch { /* fallback abajo */ }

  if (!opened) {
    const w = window.open(ext, '_blank', 'noopener,noreferrer');
    if (!w) {
      window.location.href = ext;
    }
  }
})();


createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  </StrictMode>,
)

// Le decimos a la red de seguridad de index.html que la app ha arrancado, para
// que cancele su vigilancia. Si no llegara a ejecutarse (bundle que no carga,
// error al evaluar), ese watchdog repara la instalación y recarga en vez de
// dejar la pantalla en blanco.
declare global {
  interface Window { __APP_BOOTED__?: () => void }
}
window.__APP_BOOTED__?.();
